// src/pages/EditProfile.js
import React, { useContext, useEffect, useState } from "react";
import { Box, Paper, Typography, Button, TextField } from "@mui/material";
import UserContext from "../context/UserContext";
import { formatPhoneNumber } from "../utils/phoneUtils";
import { formatName } from "../utils/nameUtils";
import api from "../services/api";;

const EditProfile = () => {
    const { user, setUser } = useContext(UserContext);
    const [editedUser, setEditedUser] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    //const navigate = useNavigate();
    const [passwordUpdated, setPasswordUpdated] = useState(false);

    useEffect(() => {
        // If full profile isn't available in context, fetch it
        const fetchFullProfile = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await api.get("/users/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
                setEditedUser(response.data);
            } catch (error) {
                console.error("Error fetching full user profile:", error);
            }
        };

        if (user) {
            // If user already has an email, assume full profile is present.
            if (!user.email) {
                fetchFullProfile();
            } else {
                setEditedUser(user);
            }
        }
    }, [user, setUser]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedUser((prev) => ({ ...prev, [name]: value }));
        if (name === 'password') {
            setPasswordUpdated(true); // Flag indicating password changed
        }
    };

    const handleEditClick = async () => {
        if (isEditing) {
            // Save updated profile
            try {
                const token = localStorage.getItem("token");
                
                const updatePayload = {
                    name: editedUser.name,
                    email: editedUser.email,
                    phone: editedUser.phone,
                    username: editedUser.username,
                    ...(passwordUpdated && { password: editedUser.password }),
                };
                //console.log("Update Payload:", updatePayload);

                const response = await api.put(
                    `/users/profile`,
                    updatePayload,
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                // Update context and local state with new data
                setUser(response.data);
                setEditedUser(response.data);
                setIsEditing(false);
                // navigate("/profile");
            } catch (error) {
                console.error("Error updating profile:", error.response?.data || error);
            }
        } else {
            // Enable editing
            setIsEditing(true);
        }
    };

    const disabledInputStyles = {
        "& .MuiInputBase-input.Mui-disabled": {
            WebkitTextFillColor: "black",
        },
    };

    return (
        <Box sx={{ pt: 8, pb: 2 }}>
            <Paper sx={{ p: 3, maxWidth: 600, mx: "auto" }}>
                <Typography variant="h5" gutterBottom>
                    Edit Profile
                </Typography>
                <TextField
                    fullWidth
                    margin="normal"
                    label="Name"
                    name="name"
                    value={editedUser.name || ""}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                        const formatted = formatName(e.target.value);
                        setEditedUser((prev) => ({ ...prev, name: formatted }));
                    }}
                    disabled={!isEditing}
                    sx={disabledInputStyles}
                />
                <TextField
                    fullWidth
                    margin="normal"
                    label="Email"
                    name="email"
                    value={editedUser.email || ""}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    sx={disabledInputStyles}
                />
                <TextField
                    fullWidth
                    margin="normal"
                    label="Phone"
                    name="phone"
                    value={formatPhoneNumber(editedUser.phone) || ""}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    sx={disabledInputStyles}
                />
                <TextField
                    fullWidth
                    margin="normal"
                    label="Username"
                    name="username"
                    value={editedUser.username || ""}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                    sx={disabledInputStyles}
                />
                <TextField
                    label="Password"
                    name="password"
                    type={isEditing ? "password" : "text"}
                    value={isEditing ? (editedUser.password || "") : "•••••••••••"}
                    onChange={(e) => {
                        handleInputChange(e);
                        setPasswordUpdated(true);
                    }}
                    onFocus={(e) => e.target.select()} // <- This will select all dots on focus
                    helperText={isEditing ? "Enter new password to update." : ""}
                    disabled={!isEditing}
                    margin="normal"
                    fullWidth
                    sx={disabledInputStyles}
                />
                <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (isEditing) {
                                handleEditClick();
                            } else {
                                setIsEditing(true);
                            }
                        }}
                    >
                        {isEditing ? "Save" : "Edit"}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default EditProfile;